const MS_PER_DAY = 1000 * 60 * 60 * 24;

export function formatSessionDate(dateStr: string, now: Date = new Date()): string {
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / MS_PER_DAY);

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${String(diffDays)}d ago`;
  return date.toLocaleDateString();
}

// ─── Temporal bucketing ───

export type TemporalBucket = 'today' | 'yesterday' | 'week' | 'older';

export const BUCKET_LABELS: Record<TemporalBucket, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  week: 'Previous 7 Days',
  older: 'Older',
};

export const BUCKET_ORDER: TemporalBucket[] = ['today', 'yesterday', 'week', 'older'];

/** Midnight-based day comparison to avoid raw-ms boundary bugs. */
function daysBetween(a: Date, b: Date): number {
  const startA = new Date(a.getFullYear(), a.getMonth(), a.getDate());
  const startB = new Date(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((startB.getTime() - startA.getTime()) / MS_PER_DAY);
}

export function getTemporalBucket(dateStr: string, now: Date = new Date()): TemporalBucket {
  const date = new Date(dateStr);
  const days = daysBetween(date, now);
  if (days === 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 7) return 'week';
  return 'older';
}

export function groupByTemporalBucket<T extends { createdAt: string }>(
  items: T[],
  now: Date = new Date(),
): Record<TemporalBucket, T[]> {
  const grouped: Record<TemporalBucket, T[]> = {
    today: [],
    yesterday: [],
    week: [],
    older: [],
  };
  for (const item of items) {
    const bucket = getTemporalBucket(item.createdAt, now);
    grouped[bucket].push(item);
  }
  return grouped;
}
